import queryString from 'querystring'

import { NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import PageLayout from 'components/pages/PageLayout'
import React, { useEffect, useMemo, useState } from 'react'
import { gql, useApolloClient, useQuery } from '@apollo/client'
import parseISO from 'date-fns/parseISO'
import getMutationErrors, {
  getMutationErrorData
} from 'utils/getMutationErrors'
import { useRouter } from 'next/router'
import toApiDateTime from 'utils/toApiDateTime'
import PageMeta from 'components/PageMeta'
import Link from 'next/link'
import {
  cateringRequestCreate,
  cateringRequestCreateVariables
} from 'components/pages/CateringRequestFormPage/types/cateringRequestCreate'
import { ArrowLeftIcon, InformationCircleIcon } from '@heroicons/react/outline'
import {
  CateringFormOrganization,
  CateringFormOrganizationVariables
} from 'components/pages/CateringRequestFormPage/types/CateringFormOrganization'
import { logEvent, setAdsUserData } from 'utils/analytics'
import { useCateringRequestFormCategories } from 'components/pages/CateringRequestFormPage/types/CateringRequestFormCategories'
import sortBy from 'lodash/sortBy'
import { OptionGroup } from 'components/ui/DeepRadioGroup'
import pickBy from 'lodash/pickBy'
import identity from 'lodash/identity'
import {
  clearSavedFormValues,
  getSavedFormValues
} from 'components/FormValuesRecorder'
import ChatWidget from 'components/ChatWidget'
import LoadingPage from 'components/pages/LoadingPage'
import { useCateringRequestPreferredTruck } from 'components/pages/CateringRequestFormPage/types/CateringRequestPreferredTruck'
import useConfirm from 'hooks/useConfirm'

import * as Types from '../../../types/graphql-global-types.d'
import useInfoDialog from '../../../hooks/useInfoDialog'
import { User } from '../../../queries/useUser'

import CateringRequestStepsForm, {
  FormValues
} from './CateringRequestStepsForm'
import { useLatestCustomerCateringRequest } from './types/LatestCustomerCateringRequest'

export const MUTATION = gql`
  mutation cateringRequestCreate(
    $address: String!
    $startAt: DateTime!
    $endAt: DateTime!
    $cateringType: String!
    $additionalInstructions: String
    $attendees: Int!
    $customerTotalBudget: Int!
    $mealParts: [MealPartsInput!]!
    $organizationId: Int
    $preferredTruckId: Int
    $creationNote: String
    $quoteOnly: Boolean
    $copyFrom: Int
    $preferredContactMethod: CateringPreferredContactMethod
    $draft: Boolean
    $fixedLocation: Boolean
  ) {
    cateringRequestCreate(
      address: $address
      startAt: $startAt
      endAt: $endAt
      cateringType: $cateringType
      additionalInstructions: $additionalInstructions
      attendees: $attendees
      customerTotalBudgetCents: $customerTotalBudget
      mealParts: $mealParts
      organizationId: $organizationId
      preferredTruckId: $preferredTruckId
      creationNote: $creationNote
      quoteOnly: $quoteOnly
      copyFrom: $copyFrom
      preferredContactMethod: $preferredContactMethod
      draft: $draft
      fixedLocation: $fixedLocation
    ) {
      cateringRequest {
        id
        code
        enableAlcoholCatering
        customerTotalBudget {
          cents
        }
        market {
          id
        }
      }
      draft
      errors {
        fullMessage
        key
        message
        data
      }
    }
  }
`

export const ORGANIZATION_QUERY = gql`
  query CateringFormOrganization($name: String!) {
    organization(seoName: $name) {
      id
      name
      cateringEmailCustomer
      cateringLogo {
        id
        variantUrl(format: png, height: 200, width: 300)
      }
    }
  }
`

function CateringRequestFormPage() {
  const [isClient, setIsClient] = useState(false)
  const alcoholSuggestionDialog = useConfirm()

  useEffect(() => {
    setIsClient(true)
  }, [])
  const client = useApolloClient()
  const infoDialog = useInfoDialog()
  const router = useRouter()
  const [organizationId, setOrganizationId] = useState<number | null>(null)
  const {
    organizer: organizerSeoName,
    truckId: preferredTruckId,
    event: initialCateringType,
    note: urlNote,
    copyFrom,
    copyFromJson,
    hideBackButton
  } = (router.query || {}) as {
    organizer?: string
    truckId?: string
    event?: string
    note?: string
    copyFrom?: string
    copyFromJson?: string
    hideBackButton?: 'true'
  }

  useEffect(() => {
    if (urlNote) {
      sessionStorage.setItem('cateringNote', urlNote)
    }
  }, [urlNote])

  const { data: truckData } = useCateringRequestPreferredTruck({
    variables: {
      id: preferredTruckId ? parseInt(preferredTruckId, 10) : null
    },
    skip: !preferredTruckId,
    fetchPolicy: 'cache-first'
  })

  const { data: categoriesData } = useCateringRequestFormCategories({
    fetchPolicy: 'cache-first'
  })

  const { data: latestRequestData } = useLatestCustomerCateringRequest({
    skip: typeof window === 'undefined',
    variables: {
      id: copyFrom ? parseInt(copyFrom, 10) : null
    }
  })

  const initialValues: Partial<FormValues> = useMemo(() => {
    if (copyFromJson) {
      try {
        const parsed: Partial<FormValues> = JSON.parse(copyFromJson)
        if (parsed.startAt) {
          parsed.startAt = new Date(parsed.startAt)
          parsed.date = new Date(parsed.startAt)
        } else {
          delete parsed.date
        }
        if (parsed.endAt) {
          parsed.endAt = new Date(parsed.endAt)
        }
        return {
          ...parsed
        } as Partial<FormValues>
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      }
    }
    const savedValues = getSavedFormValues<FormValues>('CATERING_FORM_VALUES', {
      date: parseISO,
      startAt: parseISO,
      endAt: parseISO
    })

    if (savedValues && Object.keys(savedValues).length > 0) {
      return savedValues
    }

    const lastRequest = latestRequestData?.cateringRequests?.records?.[0]
    if (!lastRequest) {
      return {
        preferredContactMethod:
          Types.CateringPreferredContactMethod.contact_by_phone_or_email
      }
    }

    return {
      attendees: lastRequest.attendees.toString(),
      cateringType: initialCateringType ?? lastRequest.cateringType,
      address: lastRequest.fullAddress,
      additionalInstructions: lastRequest.additionalInstructions,
      customerTotalBudget: lastRequest.customerTotalBudget.cents,
      preferredContactMethod: (lastRequest.preferredContactMethod as unknown) as Types.CateringPreferredContactMethod,
      mealParts: lastRequest.mealParts.map((part) => ({
        id: part.id,
        name: part.name,
        count: part.count
      }))
    } as Partial<FormValues>
  }, [latestRequestData, initialCateringType, copyFromJson])

  const categoryOptions: OptionGroup<string>[] = useMemo(() => {
    if (!categoriesData?.cateringCategories) return []

    return sortBy(
      categoriesData.cateringCategories.filter(
        (c) => !c.mainPage && !c.parentCategory
      ),
      'name'
    ).map(
      (c): OptionGroup<string> => {
        const options = [
          ...sortBy(c.childCategories, 'name').map((child) => ({
            label: child.name,
            value: child.name
          }))
        ]
        if (c.childCategories.length > 0) {
          options.push({
            label: `${c.name} - Other`,
            value: `${c.name} - Other`
          })
        }
        return {
          label: c.name,
          value: c.name,
          options
        }
      }
    )
  }, [categoriesData])

  const onSubmit = async (values: FormValues, user: User) => {
    const variables = {
      ...values,
      cateringType: values.cateringType,
      startAt: toApiDateTime(values.startAt),
      endAt: toApiDateTime(values.endAt),
      attendees: parseInt(values.attendees, 10),
      preferredTruckId: preferredTruckId
        ? parseInt(preferredTruckId, 10)
        : null,
      copyFrom: copyFrom ? parseInt(copyFrom, 10) : null,
      organizationId,
      creationNote: sessionStorage.getItem('cateringNote'),
      draft: false
    }

    const response = await client.mutate<
      cateringRequestCreate,
      cateringRequestCreateVariables
    >({
      mutation: MUTATION,
      variables
    })

    const errors = getMutationErrors(response, {
      ...values,
      requestInProgress: true,
      requestTooFarInAdvance: true,
      recommendDropOff: true
    })
    if (errors) {
      if (errors.requestInProgress) {
        const errorData = getMutationErrorData<{ catering_request_id: number }>(
          response,
          'requestInProgress'
        )
        errors.requestInProgress = (
          <>
            You already have an catering request in progress. If you want to
            submit another request please cancel{' '}
            <Link
              className="underline"
              href={`/catering/requests/${errorData!.catering_request_id}`}
            >
              the existing request
            </Link>{' '}
            or contact support{' '}
            <a className="underline" href="sms:+18333780040">
              (833) 378-0040
            </a>
          </>
        )
      }
      if (errors.requestTooFarInAdvance) {
        errors.requestTooFarInAdvance = (
          <>
            Please contact the BFT catering team at{' '}
            <Link
              className="underline"
              href="mailto:catering@bestfoodtrucks.com"
            >
              catering@bestfoodtrucks.com
            </Link>{' '}
            if you&apos;d like to discuss events that will be occurring more
            than 12 months from today.
          </>
        )
      }
      if (errors.recommendDropOff) {
        errors.recommendDropOff = (
          <>
            {errors.recommendDropOff}
            <br />
            <br />
            <Link
              className="underline font-medium"
              href={`/catering-drop-off/form?copyFromJson=${encodeURIComponent(
                JSON.stringify({
                  ...values,
                  startAt: values.startAt?.toISOString(),
                  endAt: values.endAt?.toISOString()
                })
              )}`}
            >
              Submit a drop off request
            </Link>
          </>
        )
      }
      return errors
    }

    if (variables.draft) {
      logEvent('generate_lead', {
        value: variables.customerTotalBudget / 100,
        currency: 'USD',
        form_name: 'catering_request'
      })

      await router.push('/hire-food-truck')

      infoDialog({
        title: 'Draft Saved',
        content: (
          <div className="prose antialiased mt-6">
            <p>
              The draft of this catering request was sent to your email address.
              Whenever you are ready to continue, you can click the link in the
              email to resume the request.
            </p>
            <p>
              <strong>-The BFT Team</strong>
            </p>
          </div>
        )
      })
    } else {
      const cateringRequest = response.data!.cateringRequestCreate!
        .cateringRequest!

      await router.push({
        pathname: `/catering/requests/${cateringRequest.code}`,
        query: preferredTruckId
          ? {
              truckId: preferredTruckId
            }
          : {}
      })

      if (cateringRequest.enableAlcoholCatering) {
        alcoholSuggestionDialog({
          cancelButton: 'No, thanks',
          color: 'primary',
          okButton: 'Yes, bring on the cocktails!',
          size: 'md',
          title:
            'Would you like to add Full Bar Catering Services to your event?',
          message:
            'Bartenders, beer, wine & liquor. Browse options & receive a quote now!'
        }).then((result) => {
          if (result) {
            window.open(
              `${window.location.origin}/catering/bar-service?catering=${cateringRequest.code}`,
              '_blank',
              'noopener,noreferrer'
            )
          }
        })
      }

      await setAdsUserData(user)

      logEvent('purchase', {
        transaction_id: cateringRequest.id,
        currency: 'USD',
        value: cateringRequest.customerTotalBudget.cents / 100,
        purchase_type: 'catering_request',
        items: [
          {
            item_id: 'catering_request',
            item_name: 'Catering Request'
          }
        ]
      })

      // catering_request_ads
      logEvent('conversion', {
        transaction_id: cateringRequest.id,
        currency: 'USD',
        value: cateringRequest.customerTotalBudget.cents / 100,
        send_to: 'AW-768772864/qu_7CPzVnokZEICWyu4C',
        items: [
          {
            item_id: 'catering_request',
            item_name: 'Catering Request'
          }
        ]
      })

      clearSavedFormValues('CATERING_FORM_VALUES')
    }

    return undefined
  }

  const { data: organizationData } = useQuery<
    CateringFormOrganization,
    CateringFormOrganizationVariables
  >(ORGANIZATION_QUERY, {
    variables: {
      name: organizerSeoName!
    },
    fetchPolicy: 'cache-first',
    skip: !organizerSeoName || !organizerSeoName.length
  })

  useEffect(() => {
    if (organizationData?.organization) {
      setOrganizationId(organizationData?.organization.id)
    }
  }, [organizationData])

  const meta = (
    <PageMeta
      canonicalPath="/catering/form"
      noIndex={!!organizerSeoName}
      title="Best Food Trucks | Hire a food truck"
    />
  )

  if (!isClient) {
    return <LoadingPage hideNavigation={!!organizerSeoName} meta={meta} />
  }

  const serializedQuery = queryString.stringify(
    pickBy(
      {
        truckId: preferredTruckId,
        event: initialCateringType
      },
      identity
    )
  )

  return (
    <>
      <ChatWidget />
      <PageLayout
        hideNavigation={!!organizerSeoName}
        meta={meta}
        gray
        hideFooter
      >
        <div>
          {!hideBackButton && (
            <div className="absolute top-20 left-0 right-0">
              <div className="max-w-7xl mx-auto lg:mt-16 xl:mt-10 px-4">
                <Link
                  href={
                    organizerSeoName
                      ? `/catering/by/${organizerSeoName}/form?${serializedQuery}`
                      : `/hire-food-truck?${serializedQuery}`
                  }
                >
                  <div className="max-w-max flex flex-row group items-center px-2 py-1 md:px-4 md:py-2 bg-gray-100 rounded-md transition-all duration-200">
                    <ArrowLeftIcon className="text-gray-400 w-4 h-4 mr-2 group-hover:text-gray-500" />
                    <p className="text-sm text-gray-500 group-hover:text-gray-600">
                      Back to event selection
                    </p>
                  </div>
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col max-w-screen-sm w-full pb-16">
          <div className="m-4 md:m-0">
            <div className="mt-16 xl:mt-10">
              {organizationData && organizationData.organization?.cateringLogo && (
                <div className="flex items-center justify-center mt-10 mb-6">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    alt={`${organizationData.organization.name} logo`}
                    className="bg-center object-cover"
                    src={organizationData.organization?.cateringLogo.variantUrl}
                  />
                </div>
              )}

              <h1 className="font-blockBertholdBQ uppercase text-orange-600 sm:text-6xl text-4xl whitespace-nowrap text-center">
                Catering Request
              </h1>
              <div className="text-gray-900 font-blockBertholdBQ text-lg text-center mt-2 mb-6">
                I&apos;d like to hire a food truck to cater my event(s) and
                myself or another host will be paying for all of the meals.
              </div>

              {truckData?.truck && (
                <div className="text-center mb-8 border rounded-full border-orange-200 bg-orange-50 py-2 px-4 max-w-max mx-auto">
                  <span className="font-light text-center mb-8">
                    My preferred truck is{' '}
                  </span>
                  <span className="text-xl font-bold font-blockBertholdBQ text-orange-600">
                    {truckData.truck.name}
                  </span>
                </div>
              )}
            </div>
            <CateringRequestStepsForm
              categoryOptions={categoryOptions}
              initialCateringType={initialCateringType}
              initialValues={initialValues}
              onSubmit={onSubmit}
            />
            {organizationData && (
              <div className="flex flex-row my-10 max-w-xl mx-auto">
                <div>
                  <InformationCircleIcon className="text-gray-800 w-6 h-6" />
                </div>

                <p className="text-justify text-gray-900 font-light px-2">
                  This catering is organized by{' '}
                  <span className="font-bold">
                    {organizationData.organization?.name}
                  </span>
                  . If you&apos;d like to discuss your needs with one of our
                  booking specialists directly, you can send an email to:{' '}
                  <a
                    className="underline font-bold"
                    href={`mailto:${
                      organizationData.organization?.cateringEmailCustomer ||
                      'sales@bestfoodtrucks.com'
                    }`}
                  >
                    {organizationData.organization?.cateringEmailCustomer ||
                      'sales@bestfoodtrucks.com'}
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </PageLayout>
    </>
  )
}

export default withApollo({ ssr: true })(CateringRequestFormPage as NextPage)
